import { numberWithSpaces } from "../../../../lib/format-number";
import {
  FieldControl,
  FieldFooter,
  FieldLabel,
  FieldShell,
  getInputClassName,
  unlabeledAriaProps,
} from "../../field-ui";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useId,
  useRef,
  useState,
} from "react";
import type { FormFieldInputProps, FormFieldRef } from "../../types";
import { reportFieldValue } from "../../../../lib/forms/reportFieldValue";

interface TextInputProps extends FormFieldInputProps {
  placeholder: string;
  labelMuted?: boolean;
  className?: string;
  type?: string;
  footertext?: string;
  defaultValue?: string;
  validation?: (value: string) => boolean;
}

const TextInput = forwardRef<FormFieldRef, TextInputProps>((props, ref) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [value, setValue] = useState(
    props.initialValue ?? props.defaultValue ?? "",
  );
  const [validationState, setValidationState] = useState(true);

  const commitValue = (next: string) => {
    setValue(next);
    reportFieldValue(props.fieldName, next, props.onValueChange);
  };

  useImperativeHandle(ref, () => ({
    setValidationState,
    value,
    fieldId: props.fieldId,
    fieldName: props.fieldName,
    scrollToElement: (options) => {
      shellRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        ...options,
      });
    },
    validate,
    clear,
  }));

  useEffect(() => {
    if (props.defaultValue) commitValue(props.defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync default once
  }, [props.defaultValue]);

  const clear = () => commitValue("");

  const validate = () => {
    if (!props.validation) return true;
    const result = props.validation(value);
    setValidationState(result);
    return result;
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValidationState(true);
    if (props.type === "number") {
      const n = numberWithSpaces(e.target.value.replace(/[^0-9.]/g, ""));
      commitValue(n === "0" ? "" : n);
    } else {
      commitValue(e.target.value);
    }
  };

  return (
    <FieldShell ref={shellRef} className={props.className}>
      <FieldLabel htmlFor={inputId} muted={props.labelMuted}>
        {props.placeholder}
      </FieldLabel>
      <FieldControl>
        <input
          ref={inputRef}
          id={inputId}
          value={value}
          onChange={handleOnChange}
          onBlur={validate}
          type="text"
          name={props.fieldName}
          autoComplete="off"
          aria-invalid={!validationState}
          {...unlabeledAriaProps(props.placeholder, props.accessibleName)}
          className={getInputClassName(validationState)}
        />
      </FieldControl>
      <FieldFooter>
        {!validationState ? (
          <span className="text-red-600" role="alert">
            Заполните поле
          </span>
        ) : props.footertext ? (
          <span className="text-slate-500">{props.footertext}</span>
        ) : null}
      </FieldFooter>
    </FieldShell>
  );
});

TextInput.displayName = "TextInput";

export { TextInput };
export type { TextInputProps };
